// `BrandPanel` — left panel of `AuthLayout`.
//
// DESIGN.md §0.2 + O3: token-driven CSS gradient mesh + inline SVG wave
// motif. Zero raster assets, zero DS-gap. The panel collapses to a 96px
// header strip on mobile (handled by parent layout — see `AuthLayout`).
//
// We avoid inline-style hardcodes outside what the DS Banner / Toast already
// do (CSS custom properties). The colors come from DS tokens.

import type { CSSProperties, ReactNode } from 'react';

import { useT } from '@barel/i18n';
import { WaveLogo } from '@barel/ui';

export type BrandPanelHeadlineKey =
  | 'auth.layout.brandHeadlineSignup'
  | 'auth.layout.brandHeadlineLogin'
  | 'auth.layout.brandHeadlineNeutral';

export interface BrandPanelProps {
  /** Headline i18n key (without the namespace prefix). */
  headlineKey: 'brandHeadlineSignup' | 'brandHeadlineLogin' | 'brandHeadlineNeutral';
  /** Optional extra slot below the body (e.g., illustration). */
  children?: ReactNode;
}

const PANEL_STYLE: CSSProperties = {
  // Layered radial gradients — pure tokens. The two soft circles + a subtle
  // linear wash combine to fake a "mesh".
  background:
    'radial-gradient(120% 90% at 0% 0%, color-mix(in oklab, var(--accent-default) 22%, transparent) 0%, transparent 60%),' +
    'radial-gradient(80% 60% at 100% 100%, color-mix(in oklab, var(--magenta) 18%, transparent) 0%, transparent 60%),' +
    'linear-gradient(135deg, var(--bg-surface) 0%, var(--bg-base) 100%)',
  position: 'relative',
  overflow: 'hidden',
};

/** Inline SVG flourish — sized via parent. Decorative (`aria-hidden`). */
function WaveMotif() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 600 200"
      preserveAspectRatio="none"
      style={{
        position: 'absolute',
        bottom: 'var(--space-6)',
        left: 'var(--space-6)',
        right: 'var(--space-6)',
        height: '120px',
        opacity: 0.65,
        pointerEvents: 'none',
      }}
    >
      <defs>
        <linearGradient id="ls-auth-wave-grad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="var(--accent-default)" stopOpacity="0.45" />
          <stop offset="50%" stopColor="var(--magenta)" stopOpacity="0.35" />
          <stop offset="100%" stopColor="var(--accent-default)" stopOpacity="0.20" />
        </linearGradient>
      </defs>
      <path
        d="M0 120 C 100 60, 200 180, 300 120 S 500 60, 600 120 L 600 200 L 0 200 Z"
        fill="url(#ls-auth-wave-grad)"
      />
      <path
        d="M0 140 C 100 90, 200 200, 300 150 S 500 100, 600 150 L 600 200 L 0 200 Z"
        fill="url(#ls-auth-wave-grad)"
        opacity="0.55"
      />
    </svg>
  );
}

export function BrandPanel({ headlineKey, children }: BrandPanelProps) {
  const t = useT('auth');
  return (
    <div
      data-region="auth-brand-panel"
      className="relative flex h-full flex-col gap-6 px-8 py-10 text-primary"
      style={PANEL_STYLE}
    >
      <div className="relative z-10 flex items-center gap-3">
        <WaveLogo size={32} aria-label="Barel" />
        <span className="text-md font-semibold text-primary">Barel</span>
      </div>

      <div className="relative z-10 mt-6 flex max-w-md flex-col gap-3">
        <h2 className="text-2xl font-semibold leading-tight text-primary">
          {t(`layout.${headlineKey}`)}
        </h2>
        <p className="text-sm leading-relaxed text-secondary">{t('layout.brandBody')}</p>
      </div>

      {children}

      <WaveMotif />

      <div className="relative z-10 mt-auto flex flex-col gap-1 text-xs text-tertiary">
        <span>{t('layout.copyright')}</span>
      </div>
    </div>
  );
}
